/**
 * IDP Setup - Main Component
 * Provides tabbed interface for IDP configuration management
 */

import React, { useState, useEffect } from 'react';
import authService from '../../../assets/scripts/auth/authService';
import apiClient from '../../../assets/scripts/services/apiClient';
import AuthSettingsForm from './AuthSettingsForm';
import IdpConfigList from './IdpConfigList';
import AppIdpAccess from './AppIdpAccess';
import UserIdentityManager from './UserIdentityManager';
import IdpTestTab from './IdpTestTab';

const IdpSetup = () => {
  const [activeSection, setActiveSection] = useState('auth-settings');
  const [orgId, setOrgId] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const init = async () => {
      const ok = await authService.requireAuth();
      if (!ok) return;

      try {
        setLoading(true);
        const orgs = await apiClient.get('/organizations');
        setOrganizations(Array.isArray(orgs) ? orgs : []);
        if (orgs && orgs.length > 0) {
          setOrgId(orgs[0].id);
        }
      } catch (e) {
        console.error('Failed to load organizations:', e);
        setError('Failed to load organizations');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  // Load applications when org changes
  useEffect(() => {
    if (!orgId) {
      setApplications([]);
      return;
    }

    const loadApps = async () => {
      try {
        const response = await apiClient.get(`/organizations/${orgId}/applications`);
        const apps = response?.applications || response || [];
        setApplications(Array.isArray(apps) ? apps : []);
      } catch (e) {
        console.error('Failed to load applications:', e);
      }
    };
    loadApps();
  }, [orgId]);

  const sections = [
    { id: 'auth-settings', label: 'Auth Settings', icon: 'ti-settings' },
    { id: 'idp-configs', label: 'Identity Providers', icon: 'ti-id-badge' },
    { id: 'app-access', label: 'App Access', icon: 'ti-layout-grid2' },
    { id: 'user-identity', label: 'User Identity', icon: 'ti-user' },
    { id: 'test-auth', label: 'Test Auth', icon: 'ti-check-box' },
  ];

  if (loading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  return (
    <div className="idp-setup">
      {/* Organization Selector */}
      <div className="row mB-20">
        <div className="col-md-4">
          <label className="form-label fw-bold">Organization</label>
          <select
            className="form-select"
            value={orgId || ''}
            onChange={(e) => setOrgId(Number(e.target.value))}
          >
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>
                {org.detail?.org_name || org.name || `Organization ${org.id}`}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Section Tabs */}
      <ul className="nav nav-tabs mB-20">
        {sections.map((section) => (
          <li className="nav-item" key={section.id}>
            <button
              className={`nav-link ${activeSection === section.id ? 'active' : ''}`}
              onClick={() => setActiveSection(section.id)}
            >
              <i className={`${section.icon} mR-5`}></i>
              {section.label}
            </button>
          </li>
        ))}
      </ul>

      {/* Section Content */}
      <div className="section-content">
        {activeSection === 'auth-settings' && orgId && (
          <AuthSettingsForm orgId={orgId} />
        )}
        {activeSection === 'idp-configs' && orgId && (
          <IdpConfigList orgId={orgId} />
        )}
        {activeSection === 'app-access' && orgId && (
          <AppIdpAccess orgId={orgId} applications={applications} />
        )}
        {activeSection === 'user-identity' && orgId && (
          <UserIdentityManager orgId={orgId} />
        )}
        {activeSection === 'test-auth' && orgId && (
          <IdpTestTab orgId={orgId} applications={applications} />
        )}
      </div>
    </div>
  );
};

export default IdpSetup;
